import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { not, or } from 'tio-ui/utilities';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import { PhCheck } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import LrapEnrollmentPromissoryNote from './steps/promissory-note';
import LrapEnrollmentContactInfo from './steps/contact-info';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import type PartnerService from 'tio-employee/services/partner';
import type { Store as StoreService } from '@warp-drive/core';
import { submitLrapEnrollmentRequest } from 'tio-data/requests/enrollment';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PointGroup } from 'signature_pad';
import type { LrapDemographicsConfig } from 'tio-data/data/resources/company-setting.type';

type StepId = 'mpn' | 'contact-info';

interface WizardStep {
  id: StepId;
  label: string;
  stepNumber: number;
  state: 'past' | 'active' | 'future';
}

interface WizardSignature {
  Args: {
    person: PersonModel;
  };
}

export default class LrapEnrollmentWizard extends Component<WizardSignature> {
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service declare intl: IntlService;
  @service declare store: StoreService;

  @tracked currentStepIndex = 0;

  @tracked mpnPdfSrc = '';
  @tracked mpnSignatureDataUrl = '';
  @tracked mpnSignatureErrors: string[] = [];

  @tracked contactConfirmed = false;

  @tracked selectedGender: string | null = null;
  @tracked selectedRace: string | null = null;
  @tracked selectedEthnicity: string | null = null;
  @tracked selectedVeteranStatus: string | null = null;
  @tracked additionalDemographics: Record<string, string> = {};

  get demographicsConfig(): LrapDemographicsConfig | undefined {
    return this.partner.settingModules?.lrap?.demographics;
  }

  get steps(): WizardStep[] {
    const base: Array<{ id: StepId; label: string }> = [
      { id: 'contact-info', label: this.intl.t('lrap.enrollment.contact_info.step_label') },
      { id: 'mpn', label: this.intl.t('lrap.enrollment.mpn.step_label') },
    ];
    return base.map((step, index) => ({
      ...step,
      stepNumber: index + 1,
      state:
        index < this.currentStepIndex
          ? 'past'
          : index === this.currentStepIndex
            ? 'active'
            : 'future',
    }));
  }

  get currentStep(): WizardStep {
    return this.steps[this.currentStepIndex]!;
  }

  get isFirstStep() {
    return this.currentStepIndex === 0;
  }

  get isLastStep() {
    return this.currentStepIndex === this.steps.length - 1;
  }

  get canAdvance() {
    const id = this.currentStep.id;
    if (id === 'mpn') return this.mpnSignatureDataUrl !== '';
    if (id === 'contact-info') return this.contactConfirmed;
    return true;
  }

  onMpnSignatureUpdate = (_points: PointGroup[], dataUrl: string) => {
    this.mpnSignatureDataUrl = dataUrl;
    this.mpnSignatureErrors = [];
  };

  setContactConfirmed = (confirmed: boolean) => {
    this.contactConfirmed = confirmed;
  };

  setDemographic = (field: 'gender' | 'race' | 'ethnicity' | 'veteranStatus', value: string) => {
    if (field === 'gender') this.selectedGender = value || null;
    if (field === 'race') this.selectedRace = value || null;
    if (field === 'ethnicity') this.selectedEthnicity = value || null;
    if (field === 'veteranStatus') this.selectedVeteranStatus = value || null;
  };

  setAdditionalDemographic = (key: string, value: string) => {
    this.additionalDemographics = { ...this.additionalDemographics, [key]: value };
  };

  goBack = () => {
    this.currentStepIndex -= 1;
  };

  advance = () => {
    if (!this.canAdvance) {
      if (this.currentStep.id === 'mpn') {
        this.mpnSignatureErrors = [this.intl.t('lrap.enrollment.mpn.signature_required')];
      }
      return;
    }
    if (this.isLastStep) {
      this.submitEnrollment.perform();
    } else {
      // TODO: When transitioning to the mpn step, fetch the MPN PDF URL from the API and set this.mpnPdfSrc.
      this.currentStepIndex += 1;
    }
  };

  submitEnrollment = dropTask(async () => {
    this.mpnSignatureErrors = [];
    // Cleared Select values arrive as '' (Select calls @onChange('') on clear).
    // Send those as null so additional demographics report a cleared value the
    // same way the core fields (gender/race/etc.) do, instead of POSTing an
    // ambiguous empty string.
    const additionalDemographics: Record<string, string | null> = {};
    for (const [key, value] of Object.entries(this.additionalDemographics)) {
      additionalDemographics[key] = value || null;
    }
    try {
      await this.store.request(
        submitLrapEnrollmentRequest({
          mpnSignatureDataUrl: this.mpnSignatureDataUrl,
          demographics: {
            gender: this.selectedGender,
            race: this.selectedRace,
            ethnicity: this.selectedEthnicity,
            veteranStatus: this.selectedVeteranStatus,
            ...additionalDemographics,
          },
        })
      );
    } catch {
      this.mpnSignatureErrors = [this.intl.t('lrap.enrollment.submit_error')];
      return;
    }
    // TODO: Redirect to the LRAP dashboard once it has its own route.
    await this.router.transitionTo('authenticated.slr.dashboard');
  });

  <template>
    <div class="tio-col" data-test-lrap-enrollment-wizard>
      <nav class="tio-lrap-enrollment-steps" aria-label={{t "lrap.enrollment.steps_nav_label"}}>
        {{#each this.steps as |step|}}
          <div
            class="tio-lrap-enrollment-step tio-lrap-enrollment-step--{{step.state}} tio-row-tight"
            aria-current={{if (eq step.state "active") "step"}}
          >
            <span class="tio-lrap-enrollment-step__number" aria-hidden="true">
              {{#if (eq step.state "past")}}
                <PhCheck @size={{16}} />
              {{else}}
                {{step.stepNumber}}
              {{/if}}
            </span>
            <span class="tio-lrap-enrollment-step__label">{{step.label}}</span>
          </div>
        {{/each}}
      </nav>

      <div class="tio-lrap-enrollment-body">
        {{#if (eq this.currentStep.id "contact-info")}}
          <LrapEnrollmentContactInfo
            @person={{@person}}
            @confirmed={{this.contactConfirmed}}
            @onConfirm={{this.setContactConfirmed}}
            @config={{this.demographicsConfig}}
            @selectedGender={{this.selectedGender}}
            @selectedRace={{this.selectedRace}}
            @selectedEthnicity={{this.selectedEthnicity}}
            @selectedVeteranStatus={{this.selectedVeteranStatus}}
            @onDemographicChange={{this.setDemographic}}
            @additionalDemographics={{this.additionalDemographics}}
            @onAdditionalDemographicChange={{this.setAdditionalDemographic}}
          />
        {{else if (eq this.currentStep.id "mpn")}}
          <LrapEnrollmentPromissoryNote
            @pdfSrc={{this.mpnPdfSrc}}
            @onSignatureUpdate={{this.onMpnSignatureUpdate}}
            @errors={{this.mpnSignatureErrors}}
          />
        {{/if}}
      </div>

      <div class="tio-lrap-enrollment-footer">
        {{#unless this.isFirstStep}}
          <Button
            @appearance="outlined"
            disabled={{this.submitEnrollment.isRunning}}
            {{on "click" this.goBack}}
          >
            {{t "lrap.enrollment.back"}}
          </Button>
        {{/unless}}
        <Button
          @intent="primary"
          class="tio-lrap-enrollment-footer__next"
          disabled={{or (not this.canAdvance) this.submitEnrollment.isRunning}}
          {{on "click" this.advance}}
        >
          {{#if this.submitEnrollment.isRunning}}
            {{t "lrap.enrollment.submitting"}}
          {{else if this.isLastStep}}
            {{t "lrap.enrollment.submit"}}
          {{else}}
            {{t "lrap.enrollment.continue"}}
          {{/if}}
        </Button>
      </div>
    </div>
  </template>
}
